/*
 * @Description: 自定义多标签页
 */
import { App, Badge, Dropdown, MenuProps, Space, Tabs, TabsProps } from 'antd';
import { eq, keys } from 'lodash-es';
import { FC, MutableRefObject, useCallback, useEffect, useMemo } from 'react';

import { TABSLAYOUT } from '@/utils/enums';
import type { EnumValues, UmiIcon } from '@/utils/types';
import { getRuleMenuIcon } from '@/utils';
import Icon, {
  CloseOutlined,
  RedoOutlined,
  VerticalLeftOutlined,
  VerticalRightOutlined,
} from '@ant-design/icons';
import { AccessInstance, useAccess, useAppData, useModel, matchPath, moveTab } from '@umijs/max';
import { useHotkeyContext } from './HotkeysContext';
import { CURRENT_USER_CHANNEL } from '@/constants/broadcast-channel';
import { useBroadcastSync } from 'react-simple-channel';
import useEventBus from '@/hooks/useEventBus';
import { PUSH_SINGLE_ROUTER } from '@/constants/event-bus';
import { singlePathMap } from './config';
import type { DragEndEvent } from '@dnd-kit/core';
import { closestCenter, DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React from 'react';
import SearchComponent from '../BasicLayout/components/SearchComponent';

export type TabsLayoutProps = {
  isKeep: boolean;
  keepElements: MutableRefObject<any>;
  navigate: (path: string) => void;
  dropByCacheKey: (path: string) => void;
  dropLeftTabs: (path: string) => void;
  dropRightTabs: (path: string) => void;
  dropOtherTabs: (path: string) => void;
  refreshTab: (path: string) => void;
  updateTab: (path: string, config: TabsProps) => void;
  closeTab: (path: string) => void;
  activeKey: string;
  local: { [key: string]: string };
};

const menuName = {
  close: '关闭当前',
  refresh: '重新加载',
  right: '关闭右侧',
  left: '关闭左侧',
  others: '关闭其它',
};

const TabsLayout: FC<TabsLayoutProps> = ({
  isKeep,
  keepElements,
  navigate,
  // dropByCacheKey,
  dropLeftTabs,
  dropRightTabs,
  dropOtherTabs,
  refreshTab,
  activeKey,
  local,
  closeTab,
  ...args
}) => {
  const { on } = useEventBus();

  const sleepTime = 10;

  // 暂停若干秒后执行
  const sleep = (ms: number) => {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  };

  useEffect(() => {
    on(PUSH_SINGLE_ROUTER, async ({ path, router }: { path: string; router: string[] }) => {
      const routePath = keys(keepElements.current);
      const pathList = routePath.filter((item) => {
        return router.some((r) => matchPath(r, item));
      });

      navigate(path);
      await sleep(sleepTime);

      if (pathList.length) {
        pathList.forEach((item) => {
          if (item?.toLowerCase?.() === path?.toLowerCase?.()) {
            return;
          }
          closeTab(item);
        });
      }
    });
  }, []);

  // hooks 调用
  const { message } = App.useApp();
  const { routes } = useAppData();
  const access = useAccess();
  const [currentUser] = useBroadcastSync(CURRENT_USER_CHANNEL, null);

  /**
   * @description: 点击菜单回调
   */
  const handleClickMenu = useCallback(
    (key: string, pathname: string) => {
      // const pathName = location.pathname?.toLowerCase();
      const pathName = pathname?.toLowerCase();
      switch (key) {
        case TABSLAYOUT.REFRESH:
          refreshTab(pathName);
          break;
        case TABSLAYOUT.RIGHT:
          dropRightTabs(pathName);
          break;
        case TABSLAYOUT.LEFT:
          dropLeftTabs(pathName);
          break;
        case TABSLAYOUT.OTHERS:
          dropOtherTabs(pathName);
          break;
      }
    },
    [dropLeftTabs, dropOtherTabs, dropRightTabs, refreshTab],
  );

  // 获取所有路由的正则
  const localRegex = useMemo(() => {
    const regexMap = new Map<string, RegExp>();
    Object.keys(local).forEach((routerPath) => {
      // 如果有尾巴 /，那就把尾巴 / 去掉
      const pathString = routerPath.replace(/\/$/, '');
      const regexString = pathString.replace(/:[^/]+/g, '([^/]+)');
      // 在末尾添加可选的斜杠
      regexMap.set(pathString, new RegExp(`^${regexString}/?$`));
    });
    return regexMap;
  }, [local]);

  // 通过比对路由获取路由名称
  const getRouteName = useCallback(
    (local: { [key: string]: string }, route: string): string => {
      for (const [routerRegExp, regex] of localRegex.entries()) {
        if (regex.test(route)) {
          return local[routerRegExp] || local[routerRegExp + '/'];
        }
      }
      return '';
    },
    [localRegex],
  );

  /**
   * @description: 右键菜单配置项
   */
  const renderMenuItem = (key: EnumValues<typeof TABSLAYOUT>, icon: UmiIcon | JSX.Element) => ({
    label: menuName[key],
    icon: icon,
    key: key,
  });
  const menuItems: MenuProps['items'] = [
    // 重新加载
    renderMenuItem(TABSLAYOUT.REFRESH, <RedoOutlined />),
    { type: 'divider' },
    // 关闭右侧
    renderMenuItem(TABSLAYOUT.RIGHT, <VerticalLeftOutlined />),
    // 关闭左侧
    renderMenuItem(TABSLAYOUT.LEFT, <VerticalRightOutlined />),
    { type: 'divider' },
    // 关闭其它
    renderMenuItem(TABSLAYOUT.OTHERS, <CloseOutlined />),
  ];
  /**
   * @description: Tabs 配置项
   */
  const tabsItems: TabsProps['items'] = keys(keepElements.current).map((pathname: string) => {
    // 判断是否当前活跃标签
    // const isCurrent = eq(activeKey, pathname);
    // 从菜单中获取图标
    // const icon = getRuleMenuIcon(initialState?.RouteMenu, pathname)
    // 只有当前活跃的标签页才能操作

    const dom = (
      <Space size={5}>
        {/* {isCurrent && <Badge status="processing" />} */}
        {/* {getRouteName(local, pathname)} */}
        {keepElements.current[pathname].name}
        {/* <Icon icon={icon} style={{ display: 'grid' }} /> */}
        {/* <FormattedMessage id={`menu${pathname.replaceAll('/', '.')}`} /> */}
      </Space>
    );
    return {
      key: pathname,
      label: (
        <Dropdown
          menu={{ items: menuItems, onClick: ({ key }) => handleClickMenu(key, pathname) }}
          trigger={['contextMenu']}
        >
          {dom}
        </Dropdown>
      ),
    };
  });

  // 剔除无权限的路由
  useEffect(() => {
    if (!currentUser || !access || !routes) {
      return;
    }

    // 有权限
    const accessRoutes = (Object.values(routes) as any[]).filter((route) => route?.access);
    // 无权限
    const notAccessRoutes = accessRoutes.filter(
      (route) => !access[route.access as keyof AccessInstance],
    );
    // 所有的 tabs
    const paths = Object.keys(keepElements.current);
    // 拿到不符合权限的路由
    const notAccessPath = paths.filter((pathName) =>
      notAccessRoutes.find((route) => {
        const routePath = route.path || route.originPath;
        return matchPath(routePath, pathName);
      }),
    );
    // 拿到符合权限的路由
    const accessPath = paths.filter((pathName) => !notAccessPath.includes(pathName));

    // 删除不符合权限的路由
    notAccessPath.forEach((pathName) => {
      closeTab(pathName);
    });

    // 如果当前页面就是有权限的页面，那么就停留在当前页面
    if (accessPath.includes(location.pathname)) {
      return
    }

    // 如果没有符合权限的路由，则跳转到首页
    if (accessPath.length === 0) {
      navigate('/');
    }

    // 如果有就跳转至有权限的页面
    if (accessPath.length > 0) {
      navigate(accessPath[accessPath.length - 1]);
    }

  }, [currentUser, access, routes]);

  const sensor = useSensor(PointerSensor, { activationConstraint: { distance: 10 } });
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!active || !over) {
      return;
    }

    // console.log('onDragEnd', active.id, over.id);

    if (active?.id !== over?.id) {
      moveTab(active.id as string, over.id as string);
      // setItems((prev) => {
      //   const activeIndex = prev.findIndex((i) => i.key === active.id);
      //   const overIndex = prev.findIndex((i) => i.key === over?.id);
      //   return arrayMove(prev, activeIndex, overIndex);
      // });
    }
  };
  const DraggableTabNode = ({ className, ...props }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
      id: props['data-node-key'],
    });

    const style: React.CSSProperties = {
      ...props.style,
      transform: CSS.Translate.toString(transform),
      transition,
      cursor: 'move',
    };

    return React.cloneElement(props.children as React.ReactElement<any>, {
      ref: setNodeRef,
      style,
      ...attributes,
      ...listeners,
    });
  };

  return (
    <div
      className="rumtime-keep-alive-tabs-layout qd-grid qd-grid-cols-[1fr_auto] qd-items-center qd-pl-4 qd-bg-white qd-border-b qd-border-gray-200"
      style={{ height: '56px' }}
      hidden={!isKeep}
    >
      <div className="qd-min-w-0">
        <Tabs
          hideAdd
          renderTabBar={(tabBarProps, DefaultTabBar) => (
            <DndContext sensors={[sensor]} onDragEnd={onDragEnd} collisionDetection={closestCenter}>
              <SortableContext
                items={tabsItems.map((i) => i.key)}
                strategy={horizontalListSortingStrategy}
              >
                <DefaultTabBar {...tabBarProps}>
                  {(node) => (
                    <DraggableTabNode {...node.props} key={node.key}>
                      <span>{node}</span>
                    </DraggableTabNode>
                  )}
                </DefaultTabBar>
              </SortableContext>
            </DndContext>
          )}
          onChange={(key: string) => {
            navigate(key);
          }}
          activeKey={activeKey?.toLowerCase?.()}
          type="editable-card"
          onEdit={(targetKey) => {
            // 获取当前所有 Tab 标签页的路由
            const pathNameList = keys(keepElements.current);
            // 如果只有一个 tabs ，不能关闭
            if (pathNameList.length <= 1) {
              message.info('至少要保留一个窗口');
              return;
            }
            // 获取当前 tab 的索引
            const targetIndex: number = pathNameList.indexOf(targetKey as string);
            // 清空当前页面的状态保持
            if (typeof targetKey === 'string') {
              closeTab(targetKey);
            }
            // 判断当前 tab 是否是最后一个，则向前进一位，否则向后进一位
            navigate(pathNameList[eq(targetIndex, 0) ? targetIndex + 1 : targetIndex - 1]);
          }}
          items={tabsItems}
          size="small"
          className="[&>.ant-tabs-nav]:!qd-mb-0"
        ></Tabs>
      </div>
      <div className="qd-z-50 qd-ml-4 qd-pr-4 qd-gap-1">
        <SearchComponent />
      </div>
    </div>
  );
};
export default TabsLayout;
